interface Node<T> {
  item: T;
  next?: Node<T>;
}

export type EnqueueListener<T> = (items: T[]) => void;

export class Queue<T> {
  public size: number;
  private head: Node<T> | undefined;
  private tail?: Node<T> | undefined;
  private listeners: Set<EnqueueListener<T>>;

  constructor() {
    this.head = undefined;
    this.tail = undefined;
    this.size = 0;
    this.listeners = new Set();
  }

  private _enqueue(item: T): void {
    const node: Node<T> = { item };

    this.size++;

    if (!this.tail) {
      this.tail = node;
      this.head = node;
      return;
    }

    this.tail.next = node;
    this.tail = node;
  }

  private _notifyListeners(items: T[]): void {
    for (const listener of this.listeners.values()) {
      listener(items);
    }
  }

  public enqueue(item: T): void {
    this._enqueue(item);

    this._notifyListeners([item]);
  }

  public enqueueAll(items: T[]): void {
    for (const item of items) {
      this._enqueue(item);
    }

    this._notifyListeners(items);
  }

  public dequeue(): T | undefined {
    if (!this.head) return undefined;

    this.size--;

    const head = this.head;
    this.head = head.next;

    if (!this.head) {
      this.tail = undefined;
    }

    return head.item;
  }

  public dequeueAll(): T[] {
    return this.dequeueN(Infinity);
  }

  public dequeueN(n: number): T[] {
    const items: T[] = [];

    while (this.peek() && items.length < n) {
      const item = this.dequeue();

      if (item) items.push(item);
    }

    return items;
  }

  public onEnqueue(listener: EnqueueListener<T>): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  public peek(): T | undefined {
    return this.head?.item;
  }
}

/**
 * Any items enqueued in `BatchedQueue` are enqueued in a batch request at the
 * end of the current JavaScript message queue. Consumers can also specify a
 * wait period.
 *
 * This is a similar concept to viem's request batching:
 * https://viem.sh/docs/clients/public#eth_call-aggregation-via-multicall
 */
export class BatchedQueue<T> extends Queue<T> {
  #batchedQueue = new Queue<T>();
  #isDequeuing = false;
  #wait: number;

  constructor(wait = 0) {
    super();
    this.#wait = wait;
  }

  public override enqueue(item: T): void {
    this.#batchedQueue.enqueue(item);

    if (!this.#isDequeuing) {
      this.startDequeue();
    }
  }

  public override enqueueAll(items: T[]): void {
    this.#batchedQueue.enqueueAll(items);

    if (!this.#isDequeuing) {
      this.startDequeue();
    }
  }

  private startDequeue(): void {
    if (this.#batchedQueue.size === 0) {
      return;
    }

    this.#isDequeuing = true;

    setTimeout(() => {
      const batchedItems = this.#batchedQueue.dequeueAll();

      super.enqueueAll(batchedItems);

      this.#isDequeuing = false;
    }, this.#wait);
  }
}
